import { useAtomValue, useSetAtom } from 'jotai';

import {
	type AppliedFilterItem,
	FiltersDrawerAppliedFiltersSection,
} from '@common/components/FiltersDrawer';

import { allAppliedFiltersAtom } from '../../atoms/preordersAppliedFiltersAtoms';
import {
	clearAllFiltersAtom,
	modelFilterAtom,
	statusFilterAtom,
	updateEstRetailPriceRangeAtom,
	updateModelFilterAtom,
	updateOrderDateFilterAtom,
	updatePreorderAmountRangeAtom,
	updateRegistrationStateFilterAtom,
	updateSearchValueAtom,
	updateStatusFilterAtom,
} from '../../atoms/preordersAtoms';

/**
 * PreordersFiltersDrawerAppliedFiltersSection component that displays currently applied filters
 * with the ability to remove individual filters or clear all filters
 */
export function PreordersFiltersDrawerAppliedFiltersSection() {
	const allAppliedFilters = useAtomValue(allAppliedFiltersAtom);
	const clearAllFilters = useSetAtom(clearAllFiltersAtom);
	const setSearchValue = useSetAtom(updateSearchValueAtom);
	const setStatuses = useSetAtom(updateStatusFilterAtom);
	const setModels = useSetAtom(updateModelFilterAtom);
	const setOrderDateFilter = useSetAtom(updateOrderDateFilterAtom);
	const setPreorderAmountRange = useSetAtom(updatePreorderAmountRangeAtom);
	const setEstRetailPriceRange = useSetAtom(updateEstRetailPriceRangeAtom);
	const setRegistrationState = useSetAtom(updateRegistrationStateFilterAtom);

	// Read current filter values for removal operations
	const currentStatuses = useAtomValue(statusFilterAtom);
	const currentModels = useAtomValue(modelFilterAtom);

	// Use the applied filters from the atoms with clear functions
	const allFilters: AppliedFilterItem[] = allAppliedFilters.map((filter) => ({
		...filter,
		clearFunction: getClearFunctionForFilter(filter.id),
	}));

	function getClearFunctionForFilter(filterId: string): () => void {
		// Handle status filters - extract the value from the id
		if (filterId.startsWith('status-')) {
			const value = filterId.replace('status-', '');
			return () => {
				const updatedStatuses = currentStatuses.filter((v) => v !== value);
				setStatuses(updatedStatuses);
			};
		}

		// Handle model filters - extract the value from the id
		if (filterId.startsWith('model-')) {
			const value = filterId.replace('model-', '');
			return () => {
				const updatedModels = currentModels.filter((v) => v !== value);
				setModels(updatedModels);
			};
		}

		// Handle single-value filters
		switch (filterId) {
			case 'search-query':
				return () => setSearchValue('');
			case 'order-date':
				return () => setOrderDateFilter(null);
			case 'preorder-amount':
				return () => setPreorderAmountRange({});
			case 'est-retail-price':
				return () => setEstRetailPriceRange({});
			case 'registration-state':
				return () => setRegistrationState(null);
			default:
				return () => {};
		}
	}

	const handleRemoveFilter = (filterId: string) => {
		const filter = allFilters.find((f) => f.id === filterId);
		if (filter) {
			filter.clearFunction();
		}
	};

	return (
		<FiltersDrawerAppliedFiltersSection
			allAppliedFilters={allFilters}
			clearAllFilters={clearAllFilters}
			onRemoveFilter={handleRemoveFilter}
		/>
	);
}
